/**
 * /tracking/web-to-app — Web-to-app seamless handoff (feat #182).
 *
 * Thin TanStack Router wrapper around the `WebToAppPage` feature component.
 * The root wire-up cycle picks this route up after the prod merge; agents
 * never edit `router.tsx` directly.
 *
 * Search params:
 *   - `campaign` optional campaign id to preselect in the outcomes funnel.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { WebToAppPage } from "@/features/web-to-app/WebToAppPage";
import { isValidCampaignId } from "@/features/web-to-app/api";

interface TrackingWebToAppSearch {
	campaign?: string;
}

export const trackingWebToAppRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/web-to-app",
	validateSearch: (
		raw: Record<string, unknown>,
	): TrackingWebToAppSearch => {
		const v = raw.campaign;
		if (typeof v === "string" && isValidCampaignId(v)) {
			return { campaign: v };
		}
		return {};
	},
	component: TrackingWebToAppRoute,
});

export default trackingWebToAppRoute;

// ---------------------------------------------------------------------------
// Route component — breadcrumb + the live feature page.
// ---------------------------------------------------------------------------

export function TrackingWebToAppRoute() {
	const { campaign } = trackingWebToAppRoute.useSearch();
	return (
		<div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<WebToAppPage initialCampaign={campaign} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Tracking</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Web-to-app handoff</li>
			</ol>
		</nav>
	);
}

// Named re-export for the root wire-up cycle.
export { TrackingWebToAppRoute as TrackingWebToAppPage };
